import { PdfUtils } from './PdfUtils';
import logoPixiBanner from '../assets/dixi_ponto_banner.png';
import logoEvoBanner from '../assets/logo_evo.png';
import logoTecnoBanner from '../assets/logo-tecno.png';

// Função para obter logo baseado no tema atual
const obterLogoPorTema = () => {
    const body = document.body;
    if (body.classList.contains('theme-evo')) {
        return logoEvoBanner;
    } else if (body.classList.contains('theme-tecnoponto')) {
        return logoTecnoBanner;
    }
    return logoPixiBanner; // padrão
};

class PdfAuditoria extends PdfUtils {
    /**
     * Adiciona os cabeçalhos padrão a uma nova página do PDF
     * @param {jsPDF} pdf - O objeto PDF
     * @param {string} titulo - Título do relatório
     * @param {Object} opcoes - Opções de formatação
     * @returns {number} - Posição Y inicial para o conteúdo
     */
    static adicionarCabecalhoNovasPaginas(pdf, titulo, opcoes) {
        const posicaoY = opcoes.margemTopo - 16;
        const larguraPagina = pdf.internal.pageSize.getWidth();
        
        // Adicionar logo se necessário
        if (opcoes.adicionarLogo !== false && opcoes.logoPath) {
            const logoWidth = 13; 
            const logoHeight = 7;
            const logoPosicaoY = posicaoY - 2;
            
            try {
                pdf.addImage(opcoes.logoPath, 'PNG', opcoes.margemEsquerda, logoPosicaoY, logoWidth * 1.5, logoHeight * 1.5);
            } catch (error) {
                console.error("Erro ao adicionar logo em nova página:", error);
            }
        }
        
        // Adicionar título
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 0, 0);
        pdf.text(titulo, larguraPagina / 2, posicaoY + 5, { align: 'center' });
        
        // Adicionar linha separadora
        pdf.setDrawColor(200, 200, 200);
        pdf.setLineWidth(0.5);
        pdf.line(
            opcoes.margemEsquerda, 
            posicaoY + 10, 
            larguraPagina - opcoes.margemDireita, 
            posicaoY + 10
        );
        
        // Adicionar data e hora se necessário
        if (opcoes.adicionarDataHora) {
            const dataHora = new Date().toLocaleString('pt-BR');
            pdf.setFontSize(8);
            pdf.setFont('helvetica', 'normal');
            pdf.text(dataHora, larguraPagina - opcoes.margemDireita, posicaoY - 5, { align: 'right' });
        }
        
        return posicaoY + 25; // Aumentado de 15 para 25 para dar mais espaço após o cabeçalho
    }

    static gerarRelatorioAuditoria(titulo, colunas, registros, opcoesAdicionais = {}) {
        const opcoes = {
            adicionarLogo: true,
            logoPath: obterLogoPorTema(), // Logo dinâmica baseada no tema
            adicionarDataHora: true,
            ...opcoesAdicionais
        };

        const indicativoTabela = "Tipo de Registro: ";

        const dadosFormatados = registros.map((registro) => {
            const entidadeTitulo = `Entidade: ${registro.entidade} (${registro.dataInicial} - ${registro.dataFinal})`;

            const tabelaDados = registro.registros
            .sort((a, b) => {
                const dataA = new Date(a.data.split('/').reverse().join('-')); 
                const dataB = new Date(b.data.split('/').reverse().join('-'));
                return dataA - dataB;
            })
            .map((item) => ({
                Data: item.data,
                Acao: item.acao,
                Descricao: item.descricao,
                IP: item.ip,
                Usuario: item.usuario,
                Modificacao: this.formatarTextoModificacao(item.modificacaoAuditoria)
            }));

            return { entidadeTitulo, tabelaDados };
        });

        const { pdf, inicioTabelaY, opcoes: opcoesFinais } = PdfUtils.iniciarPdf(titulo, opcoes);

        // Adicionamos um espaçamento menor para a primeira tabela
        let yAtual = inicioTabelaY + 5; // Reduzido de 10 para 5
        
        // Definir valores para controle de página
        const alturaUtil = pdf.internal.pageSize.height - 50; // Aumentado de 40 para 50 para dar mais espaço ao footer
        const alturaMediaPorLinha = 12; // Altura média de uma linha na tabela (aumentada para comportar múltiplas linhas)
        const espacoEntreTabelas = 4; // Espaço entre tabelas (aumentado de 2 para 4)
        const alturaCabecalhoTabela = 20; // Altura estimada para o cabeçalho da tabela
        const margemInferiorSegura = 20; // Margem de segurança para o footer

        dadosFormatados.forEach(({ entidadeTitulo, tabelaDados }, index) => {
            const tituloTabela = ` ${entidadeTitulo.split(': ')[1].split(' (')[0]}`;

            const linhasEntidade = tabelaDados.map((linha) => [
                linha.Data,
                linha.Acao,
                linha.Descricao,
                linha.IP,
                linha.Usuario,
                linha.Modificacao
            ]);
            
            // Estimar a altura com base no conteúdo das células usando método mais preciso
            const alturaEstimada = this.calcularAlturaTabela(linhasEntidade, alturaCabecalhoTabela, alturaMediaPorLinha);

            // Verificar se há espaço suficiente na página atual (incluindo margem de segurança)
            if (yAtual + alturaEstimada + margemInferiorSegura > alturaUtil) {
                // Adicionar nova página
                pdf.addPage();
                
                // Resetar posição Y para nova página (sem cabeçalho)
                yAtual = opcoesFinais.margemTopo || 20;
            }

            // Se não é a primeira tabela, adicionar espaço entre tabelas
            if (index > 0) {
                yAtual += espacoEntreTabelas;
            }

            opcoesFinais.ladoSemBorda = { topLeft: true };
            
            // Configurar larguras fixas das colunas
            opcoesFinais.columnStyles = {
                0: { cellWidth: 22 },  // Data - aumentado de 20 para 22
                1: { cellWidth: 25 },  // Ação
                2: { cellWidth: 30 },  // Descrição
                3: { cellWidth: 25 },  // IP
                4: { cellWidth: 25 },  // Usuário
                5: { cellWidth: 63, halign: 'left' }  // Modificação - reduzido de 65 para 63 para compensar
            };
            
            // Configurações adicionais para melhorar espaçamento
            opcoesFinais.margin = { top: 1.5, bottom: 10 }; // Aumentada ligeiramente a margem superior
            opcoesFinais.pageBreak = 'auto'; // Permite quebra de página automática
            opcoesFinais.showHead = false; // Não mostrar cabeçalho em novas páginas
            opcoesFinais.styles = {
                overflow: 'linebreak',
                cellPadding: 1, // Reduzido de 1.5 para 1
                fontSize: 9
            };

            // Adicionar a tabela na posição atual
            PdfUtils.adicionarTabela(yAtual, pdf, colunas, linhasEntidade, opcoesFinais, tituloTabela, indicativoTabela);

            // Atualizar a posição Y para a próxima tabela de forma mais precisa
            const tabelaAtual = pdf.lastAutoTable;
            if (tabelaAtual) {
                // Usar a posição real final da tabela
                yAtual = tabelaAtual.finalY;
            } else {
                // Fallback caso não consiga obter a tabela atual
                yAtual += alturaEstimada;
            }
        });

        PdfUtils.finalizarPdf(pdf, opcoesFinais);
    }

    /**
     * Calcula a altura mais precisa de uma tabela baseada no seu conteúdo
     * @param {Array} linhasEntidade - Dados da tabela
     * @param {number} alturaCabecalhoTabela - Altura do cabeçalho
     * @param {number} alturaMediaPorLinha - Altura média por linha
     * @returns {number} - Altura estimada da tabela
     */
    static calcularAlturaTabela(linhasEntidade, alturaCabecalhoTabela, alturaMediaPorLinha) {
        let alturaTotal = alturaCabecalhoTabela;
        
        linhasEntidade.forEach(linha => {
            // Calcular a altura baseada no conteúdo da coluna de modificação
            const modificacaoTexto = linha[5] || '';
            const numeroQuebrasDeLinha = (modificacaoTexto.match(/\n/g) || []).length;
            
            // Calcular altura baseada no comprimento do texto e largura fixa da coluna
            const comprimentoTexto = modificacaoTexto.length;
            const linhasEstimadas = Math.ceil(comprimentoTexto / 80); // Aumentado de 60 para 80 caracteres por linha
            
            // Usar o maior valor entre quebras explícitas e linhas estimadas
            const linhasNecessarias = Math.max(numeroQuebrasDeLinha + 1, linhasEstimadas);
            
            // Altura da linha = altura base + altura extra para linhas adicionais (reduzido o fator)
            const alturaLinha = alturaMediaPorLinha + ((linhasNecessarias - 1) * 3); // Reduzido de 4 para 3
            alturaTotal += alturaLinha;
        });
        
        return alturaTotal * 0.8; // Aplicar fator de redução de 20% para ser mais conservador
    }

    /**
     * Formata o texto de modificação para evitar que seja muito longo
     * @param {Array} modificacaoAuditoria - Array de modificações
     * @returns {string} - Texto formatado e truncado se necessário
     */
    static formatarTextoModificacao(modificacaoAuditoria) {
        if (!modificacaoAuditoria || modificacaoAuditoria.length === 0) {
            return "";
        }

        const textoCompleto = modificacaoAuditoria.map((mod) => {
            return `Descrição\nDe: ${mod.valorAntigo}\nPara: ${mod.valorNovo}`;
        }).join('\n\n');

        // Se o texto é muito longo, truncar de forma inteligente
        const limiteCaracteres = 300; // Limite de caracteres para a célula
        
        if (textoCompleto.length <= limiteCaracteres) {
            return textoCompleto;
        }

        // Truncar mantendo as informações mais importantes
        const modificacoesTruncadas = modificacaoAuditoria.map((mod) => {
            let valorAntigo = mod.valorAntigo;
            let valorNovo = mod.valorNovo;
            
            // Truncar valores individuais se muito longos
            if (valorAntigo && valorAntigo.length > 80) {
                valorAntigo = valorAntigo.substring(0, 77) + "...";
            }
            if (valorNovo && valorNovo.length > 80) {
                valorNovo = valorNovo.substring(0, 77) + "...";
            }
            
            return `De: ${valorAntigo}\nPara: ${valorNovo}`;
        });

        let textoTruncado = modificacoesTruncadas.join('\n\n');
        
        // Se ainda está muito longo, truncar globalmente
        if (textoTruncado.length > limiteCaracteres) {
            textoTruncado = textoTruncado.substring(0, limiteCaracteres - 3) + "...";
        }

        return textoTruncado;
    }
    
    /**
     * Calcula a altura aproximada necessária para exibir um texto com quebras de linha
     * @param {jsPDF} pdf - O objeto PDF
     * @param {string} texto - Texto a ser medido
     * @param {number} larguraMaxima - Largura máxima da célula em mm
     * @param {number} tamanhoFonte - Tamanho da fonte em pt
     * @returns {number} - Altura aproximada em mm
     */
    static calcularAlturaTexto(pdf, texto, larguraMaxima, tamanhoFonte = 9) {
        if (!texto) return tamanhoFonte / 2; // Altura mínima
        
        // Definir o tamanho da fonte para o cálculo
        pdf.setFontSize(tamanhoFonte);
        
        // Dividir o texto em linhas
        const linhas = texto.split('\n');
        
        // Calcular altura para todas as linhas
        let alturaTotal = 0;
        linhas.forEach(linha => {
            // Medir a largura da linha atual
            const larguraLinha = pdf.getTextWidth(linha);
            
            // Calcular quantas linhas serão necessárias para esta linha
            const numeroLinhasNecessarias = Math.ceil(larguraLinha / larguraMaxima);
            
            // Adicionar altura para esta linha (considerando que cada linha é 1.2x o tamanho da fonte)
            alturaTotal += (numeroLinhasNecessarias * (tamanhoFonte * 1.2)) / 2.83; // Conversão aproximada de pt para mm
        });
        
        return alturaTotal;
    }
}

export default PdfAuditoria;
